import { useState, useEffect } from 'react';
import { eastCretePageService } from '@/api/services';
import { resolveSiteBuilderIconComponent } from '@/utils/siteBuilderIconMap';

function mapEastCreteSections(sectionsData) {
  return (Array.isArray(sectionsData) ? sectionsData : []).map((sec) => ({
    ...sec,
    items:
      sec.items?.map((item) => ({
        ...item,
        IconComponent: resolveSiteBuilderIconComponent(item.icon),
      })) || [],
  }));
}

/**
 * Hook to fetch and manage East Crete page data
 * @param {string} sectionKey - Optional section key to fetch a specific section
 * @returns {object} { sections, section, loading, error }
 */
export const useEastCretePage = (sectionKey = null, initialSections = null) => {
  const [sections, setSections] = useState(() => (initialSections != null && !sectionKey ? mapEastCreteSections(initialSections) : []));
  const [section, setSection] = useState(null);
  const [loading, setLoading] = useState(!(initialSections != null && !sectionKey));
  const [error, setError] = useState(null);

  useEffect(() => {
    if (initialSections != null && !sectionKey) {
      setSections(mapEastCreteSections(initialSections));
      setLoading(false);
      return;
    }
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        if (sectionKey) {
          const response = await eastCretePageService.getSectionByKey(sectionKey);
          if (response.data?.success) {
            const sectionData = response.data.data;
            if (sectionData.items) {
              sectionData.items = sectionData.items.map((item) => ({
                ...item,
                IconComponent: resolveSiteBuilderIconComponent(item.icon),
              }));
            }
            setSection(sectionData);
          }
        } else {
          const response = await eastCretePageService.getAllSections();
          if (response.data?.success) {
            setSections(mapEastCreteSections(response.data.data || []));
          }
        }
      } catch (err) {
        console.error('Error fetching East Crete page data:', err);
        setError(err.message || 'Failed to load East Crete page data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [sectionKey, initialSections]);

  return { sections, section, loading, error };
};

/**
 * Get a specific section from the sections array by key
 * @param {array} sections - Array of sections
 * @param {string} key - Section key
 * @returns {object|null} Section object or null
 */
export const getSectionByKey = (sections, key) => {
  return sections.find((sec) => sec.section_key === key) || null;
};

export default useEastCretePage;
